import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags, FEATURES } from '@/contexts/FeatureFlagContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Palette, Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const THEME_OPTIONS = [
  {
    id: 'theme-1',
    name: 'Medical Teal',
    description: 'Professional healthcare look with teal accents',
    colors: ['#0EA5A4', '#2563EB', '#14B8A6'],
  },
  {
    id: 'theme-2',
    name: 'Modern Indigo',
    description: 'SaaS style with indigo and purple',
    colors: ['#6366F1', '#8B5CF6', '#A855F7'],
  },
  {
    id: 'theme-3',
    name: 'Hybrid Teal-Indigo',
    description: 'Best of both worlds with teal and indigo',
    colors: ['#0EA5A4', '#6366F1', '#14B8A6'],
  },
];

const FEATURE_LIST = [
  { key: FEATURES.DASHBOARD, label: 'Dashboard', description: 'Overview and analytics' },
  { key: FEATURES.PATIENTS, label: 'Patients', description: 'Patient management module' },
  { key: FEATURES.APPOINTMENTS, label: 'Appointments', description: 'Appointment scheduling' },
  { key: FEATURES.OPD, label: 'OPD', description: 'Out-patient department' },
  { key: FEATURES.IPD, label: 'IPD / Ward', description: 'In-patient department and ward management' },
  { key: FEATURES.PHARMACY, label: 'Pharmacy', description: 'Pharmacy and medicine inventory' },
  { key: FEATURES.LAB, label: 'Laboratory', description: 'Lab tests and reports' },
  { key: FEATURES.BILLING, label: 'Billing', description: 'Billing and invoicing' },
  { key: FEATURES.INVENTORY, label: 'Inventory', description: 'Inventory management' },
  { key: FEATURES.HR, label: 'HR & Staff', description: 'Human resources and staff management' },
  { key: FEATURES.DOCUMENTS, label: 'Documents', description: 'Document management system' },
  { key: FEATURES.AUDIT, label: 'Audit Log', description: 'Activity audit logs' },
  { key: FEATURES.USERS, label: 'User Management', description: 'User accounts' },
  { key: FEATURES.ROLE_MANAGEMENT, label: 'Role Management', description: 'Roles and module permissions' },
  { key: FEATURES.MY_CALENDAR, label: 'My Calendar', description: 'Doctor calendar' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, isDarkMode, toggleDarkMode, changeTheme } = useTheme();
  const { features, toggleFeature } = useFeatureFlags();

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully');
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Settings</h1>
        <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
          Manage your account and application preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Appearance</TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features">Feature Toggles</TabsTrigger>
          <TabsTrigger value="hospital" data-testid="tab-hospital">Hospital Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user?.avatar} alt={user?.name} />
                  <AvatarFallback className="text-2xl">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <Button variant="outline" size="sm">Change Avatar</Button>
                  <p className="text-xs text-muted-foreground mt-2">JPG, PNG or GIF. Max 2MB</p>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" defaultValue={user?.name} data-testid="profile-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" defaultValue={user?.email} data-testid="profile-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input id="role" defaultValue={user?.role?.replace('_', ' ')} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" type="tel" placeholder="+1 234 567 8900" data-testid="profile-phone-input" />
                </div>
              </div>

              <Button onClick={handleSaveProfile} data-testid="save-profile-button">
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Theme</CardTitle>
              <CardDescription>Choose your preferred color theme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                {THEME_OPTIONS.map((themeOption) => (
                  <button
                    key={themeOption.id}
                    onClick={() => changeTheme(themeOption.id)}
                    className={cn(
                      'relative p-4 border-2 rounded-lg transition-all hover:border-primary',
                      theme === themeOption.id ? 'border-primary' : 'border-border'
                    )}
                    data-testid={`theme-option-${themeOption.id}`}
                  >
                    {theme === themeOption.id && (
                      <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-4 w-4 text-primary-foreground" weight="bold" />
                      </div>
                    )}
                    <div className="flex gap-2 mb-3">
                      {themeOption.colors.map((color, i) => (
                        <div
                          key={i}
                          className="h-8 w-8 rounded-md"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <p className="font-medium text-left">{themeOption.name}</p>
                    <p className="text-xs text-muted-foreground text-left mt-1">
                      {themeOption.description}
                    </p>
                  </button>
                ))}
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="dark-mode" className="text-base">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">Switch between light and dark themes</p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={isDarkMode}
                  onCheckedChange={toggleDarkMode}
                  data-testid="dark-mode-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Feature Toggles</CardTitle>
              <CardDescription>
                Enable or disable modules to customize your hospital management system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {FEATURE_LIST.map((feature) => (
                  <div
                    key={feature.key}
                    className="flex items-center justify-between py-3 border-b last:border-0"
                    data-testid={`feature-toggle-${feature.key}`}
                  >
                    <div className="flex-1">
                      <Label htmlFor={feature.key} className="text-base font-medium">
                        {feature.label}
                      </Label>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                    <Switch
                      id={feature.key}
                      checked={features[feature.key]}
                      onCheckedChange={() => toggleFeature(feature.key)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hospital" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hospital Configuration</CardTitle>
              <CardDescription>Manage your hospital settings and information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hospital-name">Hospital Name</Label>
                <Input id="hospital-name" defaultValue="Central Medical Hospital" data-testid="hospital-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hospital-address">Address</Label>
                <Input id="hospital-address" defaultValue="123 Healthcare Ave, Medical City" data-testid="hospital-address-input" />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="hospital-phone">Phone</Label>
                  <Input id="hospital-phone" defaultValue="+1 234 567 8900" data-testid="hospital-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hospital-email">Email</Label>
                  <Input id="hospital-email" type="email" defaultValue="info@hospital.com" data-testid="hospital-email-input" />
                </div>
              </div>
              <Button data-testid="save-hospital-button">
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}